import React from 'react';
import clsx from 'clsx';
import {
    IMG_IC_24_CHECK_ONCOLOR, IMG_IC_24_DELIVERY_ONCOLOR_DISABLED, IMG_IC_24_IC_MENU_SELECTED, IMG_IC_24_PROCESSING_ONCOLOR, IMG_IC_24_CHECK_ONCOLOR_DISABLED,
    IMG_IC_24_IC_MENU_SELECTED_DISABLED, IMG_IC_24_DELIVERY_ONCOLOR, IMG_IC_24_PROCESSING_ONCOLOR_DISABLED
} from 'images'

import { OrderStatus, OrderType } from 'models';

import style from './style.module.css';

type ProgressStep = {
    code: string;
    statuses: OrderStatus[];
    activeIcon: string;
    disabledIcon: string;
};

type OrderProgressProps = {
    type: OrderType;
    status: OrderStatus;
};

const VIEW_BOX_WIDTH = 320;
const VIEW_BOX_HEIGHT = 36;

const STEP_CENTERS = [18, 112.892, 207.777, 301.891];

const PROGRESS_STOPS = [
    65.446,
    160.334,
    254.834,
    VIEW_BOX_WIDTH,
];

const UNION_PATH =
    'M301.891 0C311.832 5.77255e-05 319.891 8.05891 319.891 18C319.891 27.9411 311.832 35.9999 301.891 36C296.328 36 291.355 33.4772 288.053 29.5134C285.995 27.043 283.212 25 279.997 25H229.671C226.456 25 223.673 27.043 221.615 29.5134C218.313 33.4772 213.34 36 207.777 36C202.215 35.9999 197.243 33.4771 193.941 29.5134C191.883 27.043 189.1 25 185.885 25H134.784C131.569 25 128.786 27.043 126.728 29.5134C123.426 33.4771 118.454 35.9999 112.892 36C107.329 36 102.356 33.4772 99.0541 29.5134C96.9962 27.043 94.2132 25 90.998 25H39.8926C36.6774 25 33.8944 27.043 31.8366 29.5133C28.5349 33.4771 23.5621 36 18 36C8.05893 36 0 27.9411 0 18C0 8.05887 8.05893 0 18 0C23.5621 2.0652e-05 28.5349 2.52286 31.8366 6.48665C33.8944 8.95704 36.6774 11 39.8926 11H90.998C94.2132 11 96.9962 8.95698 99.0541 6.48663C102.356 2.5228 107.329 0 112.892 0C118.454 9.68922e-05 123.426 2.5229 126.728 6.48663C128.786 8.95701 131.569 11 134.784 11H185.885C189.1 11 191.883 8.957 193.941 6.48662C197.243 2.52291 202.215 0.000119969 207.777 0C213.34 0 218.313 2.52281 221.615 6.48664C223.673 8.95699 226.456 11 229.671 11H279.997C283.212 11 285.995 8.95696 288.053 6.4866C291.355 2.52277 296.328 0 301.891 0Z';

const getSteps = (type: OrderType): ProgressStep[] => {
    const thirdStepStatuses: OrderStatus[] =
        type === 'COURIER' ? ['ON_WAY'] : ['WAIT'];

    return [
        {
            code: 'accepted',
            statuses: ['PROCESSING'],
            activeIcon: IMG_IC_24_PROCESSING_ONCOLOR,
            disabledIcon: IMG_IC_24_PROCESSING_ONCOLOR_DISABLED,
        },
        {
            code: 'cooking',
            statuses: ['COOKING', 'IN_PROGRESS'],
            activeIcon: IMG_IC_24_IC_MENU_SELECTED,
            disabledIcon: IMG_IC_24_IC_MENU_SELECTED_DISABLED,
        },
        {
            code: 'delivery',
            statuses: thirdStepStatuses,
            activeIcon: IMG_IC_24_DELIVERY_ONCOLOR,
            disabledIcon: IMG_IC_24_DELIVERY_ONCOLOR_DISABLED,
        },
        {
            code: 'done',
            statuses: ['DONE'],
            activeIcon: IMG_IC_24_CHECK_ONCOLOR,
            disabledIcon: IMG_IC_24_CHECK_ONCOLOR_DISABLED,
        },
    ];
};

const shouldShowProgressCap = (activeStepIndex: number): boolean => {
    return activeStepIndex >= 0 && activeStepIndex < 3;
};

const getActiveStepIndex = (
    steps: ProgressStep[],
    status: OrderStatus,
    type: OrderType,
): number => {
    if (status === 'CANCELLED') {
        return -1;
    }

    if (type === 'COURIER' && status === 'WAIT') {
        return 1;
    }

    const activeStepIndex = steps.findIndex((step) => {
        return step.statuses.includes(status);
    });

    if (activeStepIndex === -1) {
        return 0;
    }

    return activeStepIndex;
};

const getProgressWidth = (activeStepIndex: number): number => {
    if (activeStepIndex < 0) {
        return 0;
    }

    return PROGRESS_STOPS[activeStepIndex];
};

const getIconLeft = (index: number): string => {
    return `${(STEP_CENTERS[index] / VIEW_BOX_WIDTH) * 100}%`;
};

const OrderProgress = ({ type, status }: OrderProgressProps) => {
    const rawClipId = React.useId();
    const clipId = `order-progress-${rawClipId.replace(/:/g, '')}`;

    const steps = getSteps(type);
    const activeStepIndex = getActiveStepIndex(steps, status, type);
    const progressWidth = getProgressWidth(activeStepIndex);
    const showProgressCap = shouldShowProgressCap(activeStepIndex);

    return (
        <div className={style.root}>
            <svg
                className={style.shape}
                viewBox={`0 0 ${VIEW_BOX_WIDTH} ${VIEW_BOX_HEIGHT}`}
                preserveAspectRatio='xMidYMid meet'
                aria-hidden='true'
            >
                <defs>
                    <clipPath id={clipId}>
                        <rect
                            x='0'
                            y='0'
                            width={progressWidth}
                            height={VIEW_BOX_HEIGHT}
                            rx='18'
                            ry='18'
                        />
                    </clipPath>
                </defs>

                <path className={style.shapeInactive} d={UNION_PATH} />

                <path
                    className={style.shapeActive}
                    clipPath={`url(#${clipId})`}
                    d={UNION_PATH}
                />

                {showProgressCap && (
                    <circle
                        className={style.progressCap}
                        cx={progressWidth}
                        cy='18'
                        r='7'
                    />
                )}
            </svg>

            <div className={style.icons}>
                {steps.map((step, index) => {
                    const isActive = index === activeStepIndex;
                    const isCompleted = index < activeStepIndex;

                    return (
                        <div
                            className={clsx(
                                style.icon,
                                isActive && style.iconActive,
                                isCompleted && style.iconCompleted,
                            )}
                            style={{ left: getIconLeft(index) }}
                            key={step.code}
                        >
                            <img
                                className={style.iconImage}
                                src={isActive || isCompleted ? step.activeIcon : step.disabledIcon}
                                alt=''
                            />
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default OrderProgress;